import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { selectUser } from "../../../core/data/redux/slice/user"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { isSuperAdmin, canAccess } from "../../../utils/rbacHelper"
import { getAllSessions } from "../../../service/sessions"
import { getAllResultBreakdowns, type ResultBreakdown } from "../../../service/result-breakdown"
import { getAllClasses } from "../../../service/classes"
import {
  generateRollNumbers,
  getRollNumbers,
  getRollNumberLogs,
  type RollNumberRow,
  type RollNumberLogItem,
} from "../../../service/roll-number"
import { all_routes } from "../../router/all_routes"
import Table from "../../../core/common/dataTable/index"

type SessionOpt = { _id: string; title: string }
type ClassOpt = { _id: string; name: string; sections: { _id: string; name: string }[] }

const RollNumber = () => {
  const user = useSelector(selectUser)
  const currentBranch = useSelector(selectCurrentBranch)
  const canRegenerate = isSuperAdmin(user) || canAccess("rollNumber.edit", user) || canAccess("rollNumber.create", user)
  const canGenerate = isSuperAdmin(user) || canAccess("rollNumber.create", user)

  const [sessions, setSessions] = useState<SessionOpt[]>([])
  const [breakdowns, setBreakdowns] = useState<ResultBreakdown[]>([])
  const [classes, setClasses] = useState<ClassOpt[]>([])
  const [filter, setFilter] = useState({ session: "", breakdown: "", class: "", section: "" })
  const [rows, setRows] = useState<RollNumberRow[]>([])
  const [loadingRows, setLoadingRows] = useState(false)
  const [generating, setGenerating] = useState(false)

  // Regeneration modal state
  const [showRegenModal, setShowRegenModal] = useState(false)
  const [regenScope, setRegenScope] = useState<"class" | "all">("all")
  const [regenReason, setRegenReason] = useState("")
  const [regenerating, setRegenerating] = useState(false)

  // Audit history modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [historyLogs, setHistoryLogs] = useState<RollNumberLogItem[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Table pagination and filtering state
  const [searchText, setSearchText] = useState("")
  const [activeSectionTab, setActiveSectionTab] = useState<string>("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  useEffect(() => {
    // Same bug/fix as report-card and marks-tabulation: without
    // currentBranch?._id in the deps, switching campus in the top bar after
    // this page loaded left the Session list stale from the OLD campus,
    // silently resolving filter.session to the wrong campus's session id
    // once picked.
    getAllSessions({ limit: 200, status: "active" }).then((r: any) => setSessions(r?.sessions || [])).catch(() => {})
    setFilter({ session: "", breakdown: "", class: "", section: "" })
  }, [currentBranch?._id])

  useEffect(() => {
    if (!filter.session || !currentBranch?._id) { setClasses([]); return }
    // Class.sections accumulates every session's sections over the class's
    // lifetime (never pruned), AND a Class shared across campuses carries
    // every campus's same-named sections too -- passing both session and
    // branch scopes the populated sections down to just this session+campus's
    // real ones via the backend's sectionsMatchFor(), otherwise the Section
    // dropdown shows duplicate stale/cross-campus entries (e.g. "A" 2-3x).
    getAllClasses({ limit: 500, all: "true", session: filter.session, branch: currentBranch._id })
      .then((r: any) => setClasses((r?.classes || []).map((c: any) => ({ _id: c._id, name: c.name, sections: c.sections || [] }))))
      .catch(() => setClasses([]))
  }, [filter.session, currentBranch?._id])

  useEffect(() => {
    if (!filter.session || !currentBranch?._id) { setBreakdowns([]); return }
    // No status filter -- roll numbers are typically generated before marks
    // entry even starts (a "draft" breakdown), unlike Marks Tabulation which
    // only makes sense once a breakdown is "active". Restricting to active
    // here would hide exactly the breakdowns roll numbers are needed for.
    //
    // branchId is required: without it, the backend expands `session` to
    // every campus's own same-titled session and returns every campus's
    // breakdowns mixed together (same cross-campus leak fixed on Marks
    // Tabulation/Report Cards, same cause -- session titles repeat per campus).
    getAllResultBreakdowns({ session: filter.session, branchId: currentBranch._id, limit: 200 })
      .then(r => setBreakdowns((r?.data || []).filter(b => b.status !== "inactive")))
      .catch(() => setBreakdowns([]))
  }, [filter.session, currentBranch?._id])

  const setF = (k: keyof typeof filter, v: string) => {
    setFilter(s => {
      const next = { ...s, [k]: v }
      const order: (keyof typeof filter)[] = ["session", "breakdown", "class", "section"]
      const i = order.indexOf(k)
      for (let j = i + 1; j < order.length; j++) next[order[j]] = ""
      return next
    })
  }

  const selectedBreakdown = breakdowns.find(b => b._id === filter.breakdown)
  const breakdownClassIds = useMemo(() => new Set((selectedBreakdown?.classes || []).map(c => String(c._id))), [selectedBreakdown])
  const classOptions = useMemo(() => classes.filter(c => breakdownClassIds.has(c._id)), [classes, breakdownClassIds])
  const sectionOptions = useMemo(() => classOptions.find(c => c._id === filter.class)?.sections || [], [classOptions, filter.class])
  const selectedClassName = useMemo(() => classOptions.find(c => c._id === filter.class)?.name || "", [classOptions, filter.class])

  const loadRows = () => {
    if (!filter.breakdown) { setRows([]); return }
    setLoadingRows(true)
    getRollNumbers({ breakdownId: filter.breakdown, classId: filter.class || undefined, sectionId: filter.section || undefined })
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoadingRows(false))
  }

  useEffect(loadRows, [filter.breakdown, filter.class, filter.section])

  // Standard generation: Initial generation or appending late-active students
  // without touching already assigned numbers.
  const handleGenerate = async () => {
    if (!filter.breakdown) return
    setGenerating(true)
    try {
      const result = await generateRollNumbers(filter.breakdown, {
        classId: filter.class || undefined,
      })
      const skipped = result.skippedClasses?.length
        ? ` (skipped ${result.skippedClasses.length} pre-primary: ${result.skippedClasses.map(c => c.name).join(", ")})`
        : ""
      const actionVerb = result.action === "append" ? "Appended" : "Generated"
      toast.success(`${actionVerb} ${result.generated} roll number(s), strategy: ${result.strategy}${skipped}`)
      loadRows()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to generate roll numbers")
    } finally {
      setGenerating(false)
    }
  }

  // Open the regeneration modal with appropriate default scope
  const openRegenModal = () => {
    setRegenScope(filter.class ? "class" : "all")
    setRegenReason("")
    setShowRegenModal(true)
  }

  // Full regeneration: Wipes existing numbers for the selected scope and
  // renumbers active students from scratch (Girls first, then Boys). Requires reason.
  const handleRegenerate = async () => {
    if (!filter.breakdown) return
    if (!regenReason.trim()) {
      toast.error("Please enter a reason for regenerating roll numbers")
      return
    }
    setRegenerating(true)
    try {
      const classIdToPass = regenScope === "class" && filter.class ? filter.class : undefined
      const result = await generateRollNumbers(filter.breakdown, {
        force: true,
        classId: classIdToPass,
        reason: regenReason.trim(),
      })
      const skipped = result.skippedClasses?.length
        ? ` (skipped ${result.skippedClasses.length} pre-primary: ${result.skippedClasses.map(c => c.name).join(", ")})`
        : ""
      toast.success(`Regenerated ${result.generated} roll number(s), strategy: ${result.strategy}${skipped}`)
      setShowRegenModal(false)
      setRegenReason("")
      loadRows()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to regenerate roll numbers")
    } finally {
      setRegenerating(false)
    }
  }

  // Open audit history modal
  const openHistoryModal = () => {
    if (!filter.breakdown) return
    setShowHistoryModal(true)
    setLoadingHistory(true)
    getRollNumberLogs({
      breakdownId: filter.breakdown,
      classId: filter.class || undefined,
    })
      .then(setHistoryLogs)
      .catch((e: any) => {
        toast.error(e?.response?.data?.message || "Failed to load audit history")
        setHistoryLogs([])
      })
      .finally(() => setLoadingHistory(false))
  }

  const sheetUrl = (autoprint: boolean) => {
    const qp = new URLSearchParams({ breakdown: filter.breakdown })
    if (filter.class) qp.set("class", filter.class)
    if (filter.section) qp.set("section", filter.section)
    if (autoprint) qp.set("autoprint", "1")
    return `${all_routes.rollNumberSheet}?${qp.toString()}`
  }

  // Sync activeSectionTab with dropdown filter if changed
  useEffect(() => {
    setActiveSectionTab(filter.section || "all")
    setCurrentPage(1)
  }, [filter.section, filter.class, filter.breakdown])

  // Extract distinct sections for quick tab pills
  const sectionTabs = useMemo(() => {
    const map = new Map<string, { id: string; name: string; count: number }>()
    for (const r of rows) {
      if (r.section?._id) {
        if (!map.has(r.section._id)) {
          map.set(r.section._id, { id: r.section._id, name: r.section.name, count: 0 })
        }
        map.get(r.section._id)!.count++
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [rows])

  // Client-side filtering by active section tab and search query
  const filteredRows = useMemo(() => {
    let list = rows
    if (activeSectionTab !== "all") {
      list = list.filter(r => r.section?._id === activeSectionTab)
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase()
      list = list.filter(r =>
        r.rollNumber.toLowerCase().includes(q) ||
        r.studentName.toLowerCase().includes(q) ||
        (r.fatherName && r.fatherName.toLowerCase().includes(q)) ||
        (r.grNumber && r.grNumber.toLowerCase().includes(q)) ||
        (r.class?.name && r.class.name.toLowerCase().includes(q)) ||
        (r.section?.name && r.section.name.toLowerCase().includes(q))
      )
    }
    return list
  }, [rows, activeSectionTab, searchText])

  // Paginated rows for display
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredRows.slice(start, start + pageSize)
  }, [filteredRows, currentPage, pageSize])

  const activeCount = useMemo(
    () => filteredRows.filter(r => r.isActive ?? (r.status === "admitted" || !r.status)).length,
    [filteredRows]
  )
  const inactiveCount = filteredRows.length - activeCount

  // Reset to page 1 whenever search query or section tab changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchText, activeSectionTab])

  // Columns definition for the Ant Design DataTable
  const columns = useMemo(() => [
    {
      title: "SR #",
      dataIndex: "sr",
      key: "sr",
      width: 65,
      align: "center" as const,
      render: (_: any, __: any, index: number) => (
        <span className="text-muted fw-semibold">
          {(currentPage - 1) * pageSize + index + 1}
        </span>
      ),
    },
    {
      title: "Roll #",
      dataIndex: "rollNumber",
      key: "rollNumber",
      width: 120,
      align: "center" as const,
      sorter: (a: RollNumberRow, b: RollNumberRow) =>
        a.rollNumber.localeCompare(b.rollNumber, undefined, { numeric: true }),
      render: (val: string) => (
        <span className="badge bg-primary-subtle text-primary border border-primary-subtle fs-13 fw-bold px-3 py-1 font-monospace">
          {val}
        </span>
      ),
    },
    {
      title: "Student Name",
      dataIndex: "studentName",
      key: "studentName",
      width: 280,
      sorter: (a: RollNumberRow, b: RollNumberRow) =>
        a.studentName.localeCompare(b.studentName),
      render: (name: string) => (
        <div className="d-flex align-items-center">
          <div
            className="rounded-circle bg-light text-primary fw-bold d-flex align-items-center justify-content-center me-2 flex-shrink-0"
            style={{ width: 32, height: 32, fontSize: 13, border: "1px solid #dee2e6" }}
          >
            {(name || "S").charAt(0).toUpperCase()}
          </div>
          <span className="fw-semibold text-dark">{name}</span>
        </div>
      ),
    },
    {
      title: "Father's Name",
      dataIndex: "fatherName",
      key: "fatherName",
      width: 240,
      render: (father: string | null) =>
        father ? <span className="text-secondary">{father}</span> : <span className="text-muted">—</span>,
    },
    {
      title: "Class",
      dataIndex: ["class", "name"],
      key: "className",
      width: 130,
      align: "center" as const,
      sorter: (a: RollNumberRow, b: RollNumberRow) =>
        (a.class?.name || "").localeCompare(b.class?.name || ""),
      render: (_: any, r: RollNumberRow) => (
        <span className="badge bg-light text-dark border px-2 py-1 fw-medium">{r.class?.name || "—"}</span>
      ),
    },
    {
      title: "Section",
      dataIndex: ["section", "name"],
      key: "sectionName",
      width: 120,
      align: "center" as const,
      sorter: (a: RollNumberRow, b: RollNumberRow) =>
        (a.section?.name || "").localeCompare(b.section?.name || ""),
      render: (_: any, r: RollNumberRow) => (
        <span className="badge badge-soft-info fw-semibold px-2 py-1">
          Section {r.section?.name || "—"}
        </span>
      ),
    },
    {
      title: "GR #",
      dataIndex: "grNumber",
      key: "grNumber",
      width: 120,
      align: "center" as const,
      sorter: (a: RollNumberRow, b: RollNumberRow) =>
        (a.grNumber || "").localeCompare(b.grNumber || ""),
      render: (gr?: string) => (
        <span className="badge bg-light text-dark border px-2 py-1 font-monospace">
          {gr || "—"}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      align: "center" as const,
      sorter: (a: RollNumberRow, b: RollNumberRow) =>
        (a.statusLabel || "Active").localeCompare(b.statusLabel || "Active"),
      render: (_: any, r: RollNumberRow) => {
        const isAct = r.isActive ?? (r.status === "admitted" || !r.status);
        if (isAct) {
          return (
            <span className="badge badge-soft-success px-2 py-1 fw-semibold">
              Active
            </span>
          );
        }
        if (r.status === "struck_off") {
          return (
            <span className="badge badge-soft-danger px-2 py-1 fw-semibold">
              Struck Off
            </span>
          );
        }
        if (r.status === "transfer_certificate") {
          return (
            <span className="badge badge-soft-warning px-2 py-1 fw-semibold">
              TC Issued
            </span>
          );
        }
        return (
          <span className="badge badge-soft-secondary px-2 py-1 fw-semibold">
            {r.statusLabel || "Inactive"}
          </span>
        );
      },
    },
  ], [currentPage, pageSize])

  return (
    <div className="page-wrapper">
      <style>{`
        .roll-numbers-table .ant-table-thead > tr > th {
          background-color: #f8fafc !important;
          font-weight: 600 !important;
          font-size: 12px !important;
          text-transform: uppercase !important;
          color: #475569 !important;
          border-bottom: 2px solid #e2e8f0 !important;
          padding: 12px 14px !important;
        }
        .roll-numbers-table .ant-table-tbody > tr > td {
          padding: 12px 14px !important;
          border-bottom: 1px solid #f1f5f9 !important;
          vertical-align: middle !important;
        }
        .roll-numbers-table .ant-table-tbody > tr:hover > td {
          background-color: #f8fafc !important;
        }
        .roll-numbers-table .ant-table {
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          overflow: hidden;
        }
      `}</style>
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Roll Numbers</h3>
          <p className="text-muted mb-0">
            Generate roll numbers per breakdown (campus + exam term). Girls listed first, then boys, alphabetically.
            Pre-primary classes are automatically skipped.
          </p>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Session</label>
                <select className="form-select" value={filter.session} onChange={e => setF("session", e.target.value)}>
                  <option value="">— Select session —</option>
                  {sessions.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Breakdown</label>
                <select className="form-select" value={filter.breakdown} disabled={!filter.session} onChange={e => setF("breakdown", e.target.value)}>
                  <option value="">{!filter.session ? "Pick a session first" : "— Select breakdown —"}</option>
                  {breakdowns.map(b => <option key={b._id} value={b._id}>{b.title}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Class (optional)</label>
                <select className="form-select" value={filter.class} disabled={!filter.breakdown} onChange={e => setF("class", e.target.value)}>
                  <option value="">— Any class —</option>
                  {classOptions.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
                <small className="text-muted">Leave blank to generate / view every class in this breakdown</small>
              </div>
              <div className="col-md-3">
                <label className="form-label">Section (optional)</label>
                <select className="form-select" value={filter.section} disabled={!filter.class} onChange={e => setF("section", e.target.value)}>
                  <option value="">— Any section —</option>
                  {sectionOptions.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Action Toolbar Card */}
        <div className="card mb-3">
          <div className="card-body py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              {rows.length > 0 ? (
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <span className="badge badge-soft-success fs-13 px-3 py-2 fw-semibold d-inline-flex align-items-center">
                    <i className="ti ti-user-check me-2 fs-6" />
                    {rows.length} Active Roll Number{rows.length === 1 ? "" : "s"}
                  </span>
                  {selectedClassName && (
                    <span className="badge badge-soft-primary fs-13 px-3 py-2 fw-semibold">
                      Class: {selectedClassName}
                    </span>
                  )}
                  {rows[0]?.strategy && (
                    <span className="badge bg-light text-secondary border fs-13 px-3 py-2 text-capitalize">
                      Strategy: {rows[0].strategy.replace("-", " ")}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-muted small">
                  {filter.breakdown
                    ? "No roll numbers generated yet for this breakdown."
                    : "Pick a session and breakdown above to view or generate roll numbers."}
                </span>
              )}
            </div>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <button
                className="btn btn-primary btn-sm px-3 d-inline-flex align-items-center fw-medium"
                disabled={!filter.breakdown || generating || !canGenerate}
                onClick={handleGenerate}
                title="Generate initial roll numbers or append late active students"
              >
                {generating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Generating…
                  </>
                ) : (
                  <>
                    <i className="ti ti-check me-1 fs-6" />
                    Generate Roll Numbers
                  </>
                )}
              </button>
              {canRegenerate && (
                <button
                  className="btn btn-outline-danger btn-sm px-3 d-inline-flex align-items-center fw-medium"
                  disabled={!filter.breakdown || regenerating || generating}
                  onClick={openRegenModal}
                  title="Wipe and recalculate roll numbers from scratch (Girls first, then Boys)"
                >
                  <i className="ti ti-refresh me-1 fs-6" />
                  Regenerate
                </button>
              )}
              <button
                className="btn btn-outline-secondary btn-sm px-3 d-inline-flex align-items-center fw-medium"
                disabled={!filter.breakdown}
                onClick={openHistoryModal}
                title="View audit trail of past generations and regenerations"
              >
                <i className="ti ti-clock-hour-4 me-1 fs-6" />
                Audit History
              </button>
              <button
                className="btn btn-light border btn-sm px-3 d-inline-flex align-items-center fw-medium text-dark"
                disabled={!filter.breakdown || rows.length === 0}
                onClick={() => window.open(sheetUrl(false), "_blank")}
                title="Open printable roll number sheet"
              >
                <i className="ti ti-external-link me-1 fs-6" />
                Open ↗
              </button>
              <button
                className="btn btn-light border btn-sm px-3 d-inline-flex align-items-center fw-medium text-dark"
                disabled={!filter.breakdown || rows.length === 0}
                onClick={() => window.open(sheetUrl(true), "_blank")}
                title="Print roll number sheet"
              >
                <i className="ti ti-printer me-1 fs-6" />
                Print ↗
              </button>
            </div>
          </div>
        </div>

        {/* Paginated Roll Numbers Table Card */}
        <div className="card">
          <div className="card-header border-bottom py-3 px-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h5 className="card-title mb-0 fw-bold">
                {selectedClassName ? `Class ${selectedClassName} Roll Numbers` : "Generated Roll Numbers"}
              </h5>
              <div className="text-muted small mt-1">
                Showing <strong>{filteredRows.length}</strong> student{filteredRows.length === 1 ? "" : "s"} matching
                {inactiveCount > 0 ? (
                  <span className="text-danger fw-semibold ms-1">
                    ({activeCount} active, {inactiveCount} inactive / struck off)
                  </span>
                ) : (
                  <span className="ms-1">({activeCount} active)</span>
                )}
                {" "}• {pageSize} rows per page
              </div>
            </div>
          </div>

          {inactiveCount > 0 && (
            <div className="alert alert-warning py-2 px-3 mx-4 my-2 mb-0 d-flex align-items-center justify-content-between small">
              <div className="d-flex align-items-center">
                <i className="ti ti-alert-triangle me-2 fs-5 text-warning" />
                <span>
                  <strong>Notice:</strong> {inactiveCount} student{inactiveCount === 1 ? "" : "s"} with an existing roll number {inactiveCount === 1 ? "is" : "are"} now marked <strong>Struck Off / Inactive</strong>.
                  Click <strong>Regenerate</strong> to recalculate roll numbers strictly for active students.
                </span>
              </div>
            </div>
          )}

          {/* Section Filter Pills */}
          {sectionTabs.length > 1 && (
            <div className="card-header border-top-0 border-bottom bg-light-subtle py-2 px-4">
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="text-muted small fw-bold text-uppercase me-2">Sections:</span>
                <button
                  type="button"
                  className={`btn btn-xs rounded-pill px-3 py-1 fw-medium ${activeSectionTab === "all" ? "btn-dark" : "btn-light border text-dark"}`}
                  onClick={() => setActiveSectionTab("all")}
                >
                  All Sections <span className="badge bg-secondary-subtle text-secondary-emphasis rounded-pill ms-1">{rows.length}</span>
                </button>
                {sectionTabs.map(sec => (
                  <button
                    key={sec.id}
                    type="button"
                    className={`btn btn-xs rounded-pill px-3 py-1 fw-medium ${activeSectionTab === sec.id ? "btn-dark" : "btn-light border text-dark"}`}
                    onClick={() => setActiveSectionTab(sec.id)}
                  >
                    Section {sec.name} <span className="badge bg-secondary-subtle text-secondary-emphasis rounded-pill ms-1">{sec.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="card-body p-0 py-3">
            {loadingRows ? (
              <div className="text-center py-5 text-muted">
                <div className="spinner-border text-primary spinner-border-sm me-2" role="status" />
                Loading roll numbers…
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="ti ti-id-badge fs-1 d-block mb-2 text-secondary" />
                {filter.breakdown
                  ? "No roll numbers generated yet for this selection. Click 'Generate roll numbers' above to create them."
                  : "Please select a Session and Result Breakdown to view roll numbers."}
              </div>
            ) : (
              <div className="roll-numbers-table px-3">
                <Table
                  columns={columns}
                  dataSource={paginatedRows}
                  Selection={false}
                  loading={loadingRows}
                  setSearchText={setSearchText}
                  pagination={{
                    currentPage,
                    limit: pageSize,
                    total: filteredRows.length,
                    onChange: (page: number, size: number) => {
                      setCurrentPage(page)
                      if (size) setPageSize(size)
                    },
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Regeneration Confirmation Modal */}
        {showRegenModal && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.55)", zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "620px" }}>
              <div className="modal-content shadow-lg border-0">
                <div className="modal-header bg-danger-subtle border-bottom px-4 py-3">
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle bg-danger text-white d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                      style={{ width: 38, height: 38 }}
                    >
                      <i className="ti ti-alert-triangle fs-5" />
                    </div>
                    <div>
                      <h5 className="modal-title mb-0 fw-bold text-danger">
                        Regenerate Roll Numbers
                      </h5>
                      <div className="text-danger small" style={{ opacity: 0.85 }}>
                        Irreversible recalculation & renumbering
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    disabled={regenerating}
                    onClick={() => setShowRegenModal(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body p-4">
                  <div className="alert alert-danger mb-4 border-danger-subtle">
                    <div className="fw-semibold mb-1 d-flex align-items-center">
                      <i className="ti ti-alert-circle me-1 fs-6" />
                      Warning: Irreversible Renumbering Action
                    </div>
                    <div className="small">
                      Regenerating will <strong>completely delete and recalculate</strong> roll numbers for the selected scope.
                      All active students will be re-assigned numbers following the official standard:
                      <ul className="mb-1 ps-3 mt-1">
                        <li><strong>Girls first</strong> (alphabetical by full name)</li>
                        <li><strong>Boys next</strong> (alphabetical by full name)</li>
                        <li><strong>GR Number</strong> as tiebreaker</li>
                      </ul>
                      <div className="mt-1 text-danger-emphasis fw-medium">
                        Any printed examination sheets or slips with old numbers will become invalid.
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold text-dark mb-2">Target Scope</label>
                    {filter.class ? (
                      <div className="d-flex flex-column gap-2 p-3 bg-light rounded border">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="regenScope"
                            id="scopeClass"
                            checked={regenScope === "class"}
                            onChange={() => setRegenScope("class")}
                            disabled={regenerating}
                          />
                          <label className="form-check-label fw-medium text-dark" htmlFor="scopeClass">
                            Selected Class Only (<strong>{selectedClassName}</strong>)
                            <div className="text-muted small fw-normal">Only regenerates roll numbers for {selectedClassName}; other classes are preserved.</div>
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="regenScope"
                            id="scopeAll"
                            checked={regenScope === "all"}
                            onChange={() => setRegenScope("all")}
                            disabled={regenerating}
                          />
                          <label className="form-check-label fw-medium text-dark" htmlFor="scopeAll">
                            All Classes in this Breakdown
                            <div className="text-muted small fw-normal">Wipes and regenerates roll numbers across all eligible classes in this breakdown.</div>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-light rounded border text-muted small">
                        <strong>All eligible classes</strong> in this Result Breakdown will be regenerated. To target a single class, pick a class from the filter dropdown above.
                      </div>
                    )}
                  </div>

                  <div className="mb-2">
                    <label className="form-label fw-bold text-dark mb-1">
                      Reason for Regeneration <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="e.g. Correcting section numbering order, new admissions restructure, updated exam guidelines..."
                      value={regenReason}
                      onChange={e => setRegenReason(e.target.value)}
                      disabled={regenerating}
                    />
                    <div className="form-text small text-muted mt-1">
                      A mandatory reason must be provided for the permanent audit trail.
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light border-top px-4 py-3 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-light border btn-sm px-4 fw-medium text-dark"
                    disabled={regenerating}
                    onClick={() => setShowRegenModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm px-4 fw-semibold"
                    disabled={regenerating || !regenReason.trim()}
                    onClick={handleRegenerate}
                  >
                    {regenerating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" />
                        Regenerating…
                      </>
                    ) : (
                      "Confirm & Regenerate"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit History Modal */}
        {showHistoryModal && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.55)", zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable" style={{ maxWidth: "1140px", width: "95%" }}>
              <div className="modal-content shadow-lg border-0">
                <div className="modal-header bg-light border-bottom px-4 py-3">
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="ti ti-clock-hour-4 fs-4" />
                    </div>
                    <div>
                      <h5 className="modal-title mb-0 fw-bold text-dark">
                        Roll Number Generation Audit Trail
                      </h5>
                      <div className="text-muted small">
                        Historical log of all initial generations, late additions, and regenerations
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowHistoryModal(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body p-4">
                  {loadingHistory ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" />
                      <div className="text-muted mt-2 small">Loading audit trail…</div>
                    </div>
                  ) : historyLogs.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="ti ti-file-text fs-1 d-block mb-2 text-secondary opacity-50" />
                      <p className="mb-0 fw-medium">No audit history records found for this breakdown.</p>
                      <small className="text-muted">Generation logs will appear here once roll numbers are created or updated.</small>
                    </div>
                  ) : (
                    <div className="table-responsive border rounded bg-white shadow-sm">
                      <table className="table table-hover table-striped align-middle mb-0">
                        <thead className="table-light border-bottom">
                          <tr>
                            <th className="py-3 px-3 text-uppercase fs-12 fw-bold text-secondary" style={{ width: 170 }}>Date & Time</th>
                            <th className="py-3 px-3 text-uppercase fs-12 fw-bold text-secondary text-center" style={{ width: 120 }}>Action</th>
                            <th className="py-3 px-3 text-uppercase fs-12 fw-bold text-secondary" style={{ width: 140 }}>Scope</th>
                            <th className="py-3 px-3 text-uppercase fs-12 fw-bold text-secondary text-center" style={{ width: 130 }}>Strategy</th>
                            <th className="py-3 px-3 text-uppercase fs-12 fw-bold text-secondary text-center" style={{ width: 130 }}>Count</th>
                            <th className="py-3 px-3 text-uppercase fs-12 fw-bold text-secondary">Reason / Note</th>
                            <th className="py-3 px-3 text-uppercase fs-12 fw-bold text-secondary" style={{ width: 170 }}>Performed By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyLogs.map(log => {
                            const d = new Date(log.createdAt)
                            const dateStr = d.toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                            const timeStr = d.toLocaleTimeString(undefined, {
                              hour: "2-digit",
                              minute: "2-digit",
                            })

                            const isRegen = log.action === "regenerate"
                            const isAppend = log.action === "append"
                            const badgeClass = isRegen
                              ? "badge-soft-danger border border-danger-subtle text-danger"
                              : isAppend
                                ? "badge-soft-success border border-success-subtle text-success"
                                : "badge-soft-primary border border-primary-subtle text-primary"
                            const actionLabel = isRegen
                              ? "Regenerated"
                              : isAppend
                                ? "Appended"
                                : "Generated"

                            return (
                              <tr key={log._id}>
                                <td className="py-3 px-3 text-nowrap">
                                  <div className="fw-semibold text-dark fs-13">{dateStr}</div>
                                  <div className="text-muted small" style={{ fontSize: "11px" }}>
                                    <i className="ti ti-clock me-1" />
                                    {timeStr}
                                  </div>
                                </td>
                                <td className="py-3 px-3 text-center">
                                  <span className={`badge px-2 py-1 fw-semibold ${badgeClass}`}>
                                    {actionLabel}
                                  </span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="fw-medium text-dark">
                                    {log.class?.name ? `Class ${log.class.name}` : "All Classes"}
                                  </span>
                                </td>
                                <td className="py-3 px-3 text-center">
                                  <span className="badge bg-light text-secondary border px-2 py-1 text-capitalize">
                                    {log.strategy ? log.strategy.replace("-", " ") : "—"}
                                  </span>
                                </td>
                                <td className="py-3 px-3 text-center">
                                  <span className="fw-bold text-dark fs-13">{log.studentsCount}</span>
                                  <span className="text-muted small ms-1" style={{ fontSize: "11px" }}>
                                    ({log.totalCount} total)
                                  </span>
                                </td>
                                <td className="py-3 px-3">
                                  {log.reason ? (
                                    <div className="text-dark small" style={{ maxWidth: 300, wordBreak: "break-word" }}>
                                      {log.reason}
                                    </div>
                                  ) : (
                                    <span className="text-muted small italic">—</span>
                                  )}
                                </td>
                                <td className="py-3 px-3 text-nowrap">
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="rounded-circle bg-light text-secondary border d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                                      style={{ width: 28, height: 28, fontSize: 11, fontWeight: "bold" }}
                                    >
                                      {(log.performedBy?.firstName || "U").charAt(0).toUpperCase()}
                                    </div>
                                    <div className="small fw-semibold text-dark">
                                      {log.performedBy
                                        ? `${log.performedBy.firstName || ""} ${log.performedBy.lastName || ""}`.trim() || log.performedBy.email || "System"
                                        : "System"}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer bg-light border-top px-4 py-3 d-flex justify-content-between align-items-center">
                  <div className="text-muted small">
                    Total <strong>{historyLogs.length}</strong> log record{historyLogs.length === 1 ? "" : "s"}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary px-4 btn-sm fw-medium"
                    onClick={() => setShowHistoryModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default RollNumber
